import { Controller } from "@hotwired/stimulus"
import {
  buildNoticeRow,
  collectFromDataTransfer,
  uploadEntries,
  refreshFolderTotals,
  formatFileSize,
  DEFAULT_MAX_FILES,
  DEFAULT_MAX_FILE_SIZE,
} from "../../helpers/document_uploads"

export default class extends Controller {
  static targets = ["dropzone", "input", "previews", "template"]
  static values = {
    url: String,
    totalsUrl: { type: String, default: "" },
    folderId: String,
    maxFiles: { type: Number, default: DEFAULT_MAX_FILES },
    maxFileSize: { type: Number, default: DEFAULT_MAX_FILE_SIZE },
    fileTooLargeMessage: { type: String, default: "" },
    uploadFailedMessage: { type: String, default: "" },
    tooManyFilesMessage: { type: String, default: "" },
  }

  click(event) {
    // Don't handle clicks on the input itself - let the browser handle it
    if (event.target === this.inputTarget) {
      return
    }

    event.preventDefault()
    this.inputTarget.click()
  }

  dragover(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.add("border-green-400", "bg-green-50")
  }

  dragleave(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-green-400", "bg-green-50")
  }

  async drop(event) {
    event.preventDefault()
    this.dropzoneTarget.classList.remove("border-green-400", "bg-green-50")

    const entries = await collectFromDataTransfer(event.dataTransfer)
    this.processEntries(entries)
  }

  filesSelected(event) {
    const entries = Array.from(event.target.files).map((file) => ({ file, relativePath: "" }))
    this.processEntries(entries)
    // Reset input for next selection
    event.target.value = ""
  }

  async processEntries(entries) {
    if (entries.length === 0) return

    this.previewsTarget.classList.remove("hidden")

    await uploadEntries(entries, {
      url: this.urlValue,
      folderId: this.folderIdValue,
      // One totals refresh at batch end (below) instead of a recursive recompute per file.
      deferTotals: true,
      maxFiles: this.maxFilesValue,
      maxFileSize: this.maxFileSizeValue,
      fileTooLargeMessage: this.fileTooLargeMessageValue,
      uploadFailedMessage: this.uploadFailedMessageValue,
      onPreview: (file, relativePath) => this.createPreview(file, relativePath),
      onProgress: (preview, percent) => this.updateProgress(preview, percent),
      onSuccess: (preview) => this.showSuccess(preview),
      onError: (preview, error) => this.showUploadError(preview, error),
      onTruncated: (skipped, max) => this.showTruncationNotice(skipped, max),
    })

    await refreshFolderTotals(this.totalsUrlValue)
  }

  showTruncationNotice(skipped, max) {
    const message = this.tooManyFilesMessageValue
      .replace("%{count}", skipped)
      .replace("%{max}", max)

    this.previewsTarget.appendChild(buildNoticeRow(this.templateTarget, message))
  }

  createPreview(file, relativePath) {
    const template = this.templateTarget.content.cloneNode(true)
    const container = template.querySelector("[data-upload-item]")

    const displayName = relativePath ? `${relativePath}/${file.name}` : file.name
    container.querySelector("[data-filename]").textContent = displayName
    container.querySelector("[data-filesize]").textContent = formatFileSize(file.size)

    this.previewsTarget.appendChild(container)
    return container
  }

  updateProgress(preview, percent) {
    const progressBar = preview.querySelector("[data-progress]")
    if (progressBar) {
      progressBar.style.width = `${percent}%`
    }
  }

  showSuccess(preview) {
    preview.querySelector(".uploading").classList.add("hidden")
    preview.querySelector(".completed").classList.remove("hidden")

    const progressBar = preview.querySelector("[data-progress]")
    if (progressBar) {
      progressBar.style.width = "100%"
    }

    // Remove preview after a delay
    setTimeout(() => {
      preview.remove()
      if (this.previewsTarget.children.length === 0) {
        this.previewsTarget.classList.add("hidden")
        // Close the modal when all uploads are done
        this.closeModal()
      }
    }, 1000)
  }

  closeModal() {
    const modal = document.getElementById("modal")
    if (modal) {
      modal.innerHTML = ""
    }
  }

  showUploadError(preview, error) {
    preview.querySelector(".uploading").classList.add("hidden")
    preview.querySelector(".error").classList.remove("hidden")
    console.error("Upload error:", error)
  }

  removeFile(event) {
    const preview = event.target.closest("[data-upload-item]")
    preview.remove()

    if (this.previewsTarget.children.length === 0) {
      this.previewsTarget.classList.add("hidden")
    }
  }
}
